/* Oricle buy fold behaviours: gallery, stepper, arrival date, product form, sticky dock; the hearing-aid fold's pinning,
   clamp, kit, clips and in-page asks. */
(() => {
  // Loaded by the fold and by the hearing-aid page's later sections; the document listeners below bind once.
  if (window.__oriclePdpJs) return; window.__oriclePdpJs = true;
  const REDUCE = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Gallery: thumbnails swap the main frame; the swap crossfades (CSS), instant under reduced
     motion. The thumbnails are toggle buttons (aria-pressed) — not a tab list, which would
     promise arrow-key navigation and tab panels this does not have. */
  /* The hearing-aid fold's gallery (9/29) has a stage ([data-o-gal-stage]): on desktop the active frame shows by class and a
     counter follows it; on a phone the stage is a full-bleed swipe strip, dots jump to a frame and the frame scrolled into
     view becomes the active one. A playing clip pauses when its frame is left. */
  class OricleGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.stage = this.querySelector('[data-o-gal-stage]');
      this.index = 0;
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-o-gal-thumb]'); if (t) { this.show(parseInt(t.dataset.oGalThumb, 10)); return; }
        const d = e.target.closest('[data-o-gal-dot]'); if (d) this.go(parseInt(d.dataset.oGalDot, 10));
      });
      if (this.stage) {
        let ticking = false;
        this.onScroll = () => {
          if (ticking) return; ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const w = this.stage.clientWidth; if (!w || this.stage.scrollWidth <= w) return;
            const k = Math.round(this.stage.scrollLeft / w);
            if (k !== this.index) this.show(k, true);
          });
        };
        this.stage.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }
    disconnectedCallback() { if (this.stage && this.onScroll) this.stage.removeEventListener('scroll', this.onScroll); }
    go(i) {
      if (!this.stage) { this.show(i); return; }
      const w = this.stage.clientWidth;
      if (this.stage.scrollWidth > w) this.stage.scrollTo({ left: i * w, behavior: REDUCE() ? 'auto' : 'smooth' });
      this.show(i, true);
    }
    show(i, fromScroll) {
      if (Number.isNaN(i)) return;
      this.index = i;
      this.querySelectorAll('[data-o-gal-main]').forEach((m) => {
        const on = parseInt(m.dataset.oGalMain, 10) === i;
        if (!this.stage) m.hidden = !on; // the accessory and supplement folds hide frames outright
        m.classList.toggle('is-on', on);
        if (!on) m.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });
      });
      this.querySelectorAll('[data-o-gal-thumb]').forEach((t) => { const on = parseInt(t.dataset.oGalThumb, 10) === i; t.classList.toggle('is-on', on); t.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      this.querySelectorAll('[data-o-gal-dot]').forEach((d) => { const on = parseInt(d.dataset.oGalDot, 10) === i; d.classList.toggle('is-on', on); if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      const n = this.querySelector('[data-o-gal-n]'); if (n) n.textContent = String(i + 1);
      /* The badges sit on the first frame only; later frames carry their own lettering there. */
      const b = this.querySelector('.o-gal__badges'); if (b) b.hidden = i !== 0;
      /* A thumbnail picked on desktop keeps the phone strip in step if the window narrows later. */
      if (this.stage && !fromScroll && this.stage.scrollWidth > this.stage.clientWidth) this.stage.scrollLeft = i * this.stage.clientWidth;
    }
  }
  if (!customElements.get('oricle-gallery')) customElements.define('oricle-gallery', OricleGallery);

  /* Stepper: − / + by one, clamped to the input's min–max; out-of-range presses are no-ops. The
     number shown IS the form's real input, so a keyboard user can see focus on it and type a
     quantity directly. */
  class OricleStepper extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-o-qty]');
      if (!this.input || this._bound) return; // cart-line steppers are driven by the drawer
      this._bound = true;
      this.min = parseInt(this.input.min || '1', 10); this.max = parseInt(this.input.max || '9', 10);
      this.addEventListener('click', (e) => { const b = e.target.closest('[data-o-step]'); if (b) this.set(this.value + parseInt(b.dataset.oStep, 10)); });
      this.input.addEventListener('change', () => this.set(this.value));
      this.set(this.value);
    }
    get value() { const v = parseInt(this.input.value, 10); return Number.isNaN(v) ? this.min : v; }
    set(v) {
      v = Math.max(this.min, Math.min(this.max, v));
      this.input.value = v;
      const steps = this.querySelectorAll('[data-o-step]');
      if (steps[0]) { steps[0].classList.toggle('is-dim', v <= this.min); steps[0].setAttribute('aria-disabled', v <= this.min ? 'true' : 'false'); }
      if (steps[1]) { steps[1].classList.toggle('is-dim', v >= this.max); steps[1].setAttribute('aria-disabled', v >= this.max ? 'true' : 'false'); }
    }
  }
  if (!customElements.get('oricle-stepper')) customElements.define('oricle-stepper', OricleStepper);

  /* Product form: adds the device (and any add-ons ticked in the Care and cleaning row) through
     the Cart API, then hands the rendered sections to the drawer. A ticked add-on is never a
     dead end. Re-entrant submits are ignored whatever triggered them (the button, Enter, the
     dock, the lineup). Errors are plain sentences. With no drawer on the page (the merchant
     chose the "page" cart type) a successful add goes to the cart page, as Dawn's own form does.
     Without JavaScript the form still posts to /cart/add. */
  class OricleProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this._bound) return;
      this._bound = true;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.addons = Array.from(this.querySelectorAll('[data-o-addon]'));
      this.hint = this.querySelector('[data-o-addon-hint]');
      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-o-addon]');
        if (a) { a.setAttribute('aria-pressed', a.getAttribute('aria-pressed') === 'true' ? 'false' : 'true'); this.syncAddons(); return; }
        const boxToggle = e.target.closest('[data-o-open-row="box"]');
        if (boxToggle) {
          const acc = this.querySelector('oricle-accordion'); const item = this.querySelector('[data-o-row="box"]');
          if (acc && item) { acc.toggle(item); boxToggle.setAttribute('aria-expanded', item.classList.contains('is-open') ? 'true' : 'false'); }
        }
      });
    }
    syncAddons() {
      const n = this.addons.filter((a) => a.getAttribute('aria-pressed') === 'true').length;
      this.addons.forEach((a) => { const pill = a.querySelector('.o-addon__pill'); if (pill) pill.textContent = a.getAttribute('aria-pressed') === 'true' ? pill.dataset.added : pill.dataset.add; });
      if (this.hint) this.hint.textContent = n && this.hint.dataset.template ? this.hint.dataset.template.replace('{n}', n) : '';
    }
    items() {
      const fd = new FormData(this.form);
      const items = [{ id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1 }];
      // A selling plan rides along only when the form carries one (the supplement fold enables
      // its hidden input when the plan row is chosen); a device form never has it.
      const plan = parseInt(fd.get('selling_plan'), 10);
      if (plan) items[0].selling_plan = plan;
      // Line properties in the form (the PDP template test's hidden _pdp_arm / _pdp_src, enabled only for a visitor in
      // the test: snippets/oricle-pdp-test.liquid) ride on the device line, as a plain form post would send them.
      const props = {};
      fd.forEach((v, k) => { const m = /^properties\[(.+)\]$/.exec(k); if (m && v !== '') props[m[1]] = String(v); });
      if (Object.keys(props).length) items[0].properties = props;
      this.addons.filter((a) => a.getAttribute('aria-pressed') === 'true').forEach((a) => items.push({ id: parseInt(a.dataset.variantId, 10), quantity: 1 }));
      return items;
    }
    setBusy(on) {
      document.querySelectorAll('[data-o-add],[data-o-dock-add],[data-o-lineup-add],[data-o-page-add]').forEach((b) => { b.disabled = on; if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy'); });
    }
    async onSubmit(e) {
      if (!window.routes || !window.fetch) return; // let the browser post the form normally
      e.preventDefault();
      if (this._submitting) return;
      this._submitting = true;
      const err = this.querySelector('[data-o-error]');
      const generic = (err && err.dataset.generic) || 'Something went wrong adding this to your cart. Please try again.';
      const offline = (err && err.dataset.network) || 'We could not reach the cart. Please check your connection and try again.';
      this.setBusy(true);
      if (err) { err.hidden = true; err.textContent = ''; }
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'];
      let leaving = false;
      try {
        // A paid lander's form (the fold's "lander" block) goes straight to checkout after stamping the attribution cart
        // attributes (assets/oricle-lander.js); the buttons stay busy while checkout loads.
        if (this.dataset.afterAdd === 'checkout' && window.Oricle && window.Oricle.landerCheckout) {
          leaving = await window.Oricle.landerCheckout(this.form, { generic, offline });
          if (leaving) return;
        }
        let res;
        try {
          // .js: the full line reaches the analytics pixels (see add() in oricle-cart.js).
          res = await fetch(`${window.routes.cart_add_url}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: this.items(), sections, sections_url: window.location.pathname }) });
        } catch (networkError) { throw new Error(offline); }
        let data = null;
        try { data = await res.json(); } catch (parseError) { data = null; }
        if (!res.ok || !data || data.status) throw new Error((data && (data.description || data.message)) || generic);
        if (drawer && drawer.renderContents) {
          const asker = window.Oricle && window.Oricle.buyInitiator;
          if (window.Oricle) window.Oricle.buyInitiator = null;
          drawer.setActiveElement(asker && document.contains(asker) ? asker : this.form.querySelector('[data-o-add]'));
          drawer.renderContents(data, true);
        } else { window.location = window.routes.cart_url; return; }
        this.addons.forEach((a) => a.setAttribute('aria-pressed', 'false')); this.syncAddons();
        this.flashAdded();
        if (window.publish && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'oricle-product-form' });
      } catch (ex) {
        if (err) { err.textContent = ex && ex.message ? ex.message : generic; err.hidden = false; }
        // On the lander the ask may be far down the page: bring the fold's error line into view.
        if (err && this.dataset.afterAdd === 'checkout' && err.scrollIntoView) err.scrollIntoView({ block: 'center', behavior: REDUCE() ? 'auto' : 'smooth' });
      } finally {
        this._submitting = false;
        if (!leaving) this.setBusy(false);
      }
    }
    flashAdded() {
      document.querySelectorAll('[data-o-cta-label]').forEach((el) => {
        const label = el.dataset.label || el.textContent; const added = el.dataset.added || label;
        el.dataset.label = label; el.textContent = added;
        clearTimeout(el._flash); el._flash = setTimeout(() => { el.textContent = label; }, 1800);
      });
    }
  }
  if (!customElements.get('oricle-product-form')) customElements.define('oricle-product-form', OricleProductForm);

  /* Sticky dock: takes over the moment the CTA ROW scrolls off the top; never while the cart is
     open. An IntersectionObserver reports the row leaving — no scroll handler, no layout read
     per scroll event. While hidden the dock is inert, so its controls cannot take keyboard
     focus off screen. Every listener added here is removed on disconnect. */
  class OricleDock extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector(this.dataset.watch || '[data-o-cta-row]');
      // data-until-cta (rendered only by a paid lander's fold block, lander polish 10/15): the dock shows from page load
      // until the fold's buy row is on screen, then behaves as below. Without the attribute nothing here changes.
      this.untilCta = this.hasAttribute('data-until-cta');
      this.cartOpen = false; this.docked = false;
      if (this.untilCta && this.target) { const r = this.target.getBoundingClientRect(); this.docked = r.bottom < 0 || r.top > window.innerHeight; }
      this.onDrawerOpen = (e) => { if (e.target.tagName === 'CART-DRAWER') { this.cartOpen = true; this.update(); } };
      this.onDrawerClose = (e) => { if (e.target.tagName === 'CART-DRAWER') { this.cartOpen = false; this.update(); } };
      document.addEventListener('oricle:drawer:open', this.onDrawerOpen);
      document.addEventListener('oricle:drawer:close', this.onDrawerClose);
      this.onClick = (e) => { if (e.target.closest('[data-o-dock-add]') && window.Oricle && window.Oricle.submitBuyForm) window.Oricle.submitBuyForm(e.target.closest('[data-o-dock-add]')); };
      this.addEventListener('click', this.onClick);
      // Docked = the buy row is ABOVE the viewport. One observer cannot see that: on a phone the
      // row starts below the fold, and a jump past it (an anchor link such as the rating's
      // #reviews, or scroll restoration) goes from "not intersecting" to "not intersecting" — no
      // callback, no dock. So two states are observed: `inView` (the plain viewport) and
      // `reached` (a viewport stretched far upward: true once the row is in view OR above it).
      // Any move from below to above flips `reached`, however far the jump.
      this.inView = false; this.reached = false;
      const recompute = () => { this.docked = this.untilCta ? !this.inView : this.reached && !this.inView; this.update(); };
      const designMode = window.Shopify && window.Shopify.designMode;
      if (this.target && 'IntersectionObserver' in window && !designMode) {
        this.io = new IntersectionObserver((es) => { this.inView = es[es.length - 1].isIntersecting; recompute(); }, { threshold: 0 });
        this.ioAbove = new IntersectionObserver((es) => { this.reached = es[es.length - 1].isIntersecting; recompute(); }, { threshold: 0, rootMargin: '100000px 0px 0px 0px' });
        this.io.observe(this.target); this.ioAbove.observe(this.target);
      } else if (this.target) {
        // The theme editor runs the page in a cross-origin frame, where rootMargin is ignored:
        // measure on scroll there instead (one rAF per frame; the editor is not a customer's phone).
        let ticking = false;
        this.onScroll = () => { if (ticking) return; ticking = true; requestAnimationFrame(() => { ticking = false; const r = this.target.getBoundingClientRect(); this.docked = this.untilCta ? (r.bottom < 0 || r.top > window.innerHeight) : r.bottom < 0; this.update(); }); };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
      // The hearing-aid dock (data-avoid) also steps aside while any in-page ask or the footer is on screen, so one
      // primary shows per view (PdpPage.jsx, 9/29). Sections loaded later in the editor are picked up on load.
      this.asks = new Set();
      if (this.dataset.avoid && 'IntersectionObserver' in window) {
        this.ioAsk = new IntersectionObserver((es) => { es.forEach((e) => { if (e.isIntersecting) this.asks.add(e.target); else this.asks.delete(e.target); }); this.update(); }, { threshold: 0 });
        this.observeAsks = () => document.querySelectorAll(this.dataset.avoid).forEach((el) => this.ioAsk.observe(el));
        this.observeAsks();
        this.onSectionLoad = () => { this.asks.clear(); this.ioAsk.disconnect(); this.observeAsks(); this.update(); };
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }
      this.update();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.ioAbove) this.ioAbove.disconnect();
      if (this.ioAsk) this.ioAsk.disconnect();
      if (this.onSectionLoad) document.removeEventListener('shopify:section:load', this.onSectionLoad);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('oricle:drawer:open', this.onDrawerOpen);
      document.removeEventListener('oricle:drawer:close', this.onDrawerClose);
      this.removeEventListener('click', this.onClick);
    }
    update() {
      const on = this.docked && !this.cartOpen && !(this.asks && this.asks.size);
      this.classList.toggle('is-visible', on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) this.removeAttribute('inert'); else this.setAttribute('inert', '');
    }
  }
  if (!customElements.get('oricle-dock')) customElements.define('oricle-dock', OricleDock);

  /* Hearing-aid fold pinning (PdpPage.jsx PpFold, desktop only): the left column pins at --sticky-top, or by its bottom
     edge when it is taller than the window; the panel pins only when it fits the window, otherwise it scrolls and the dock
     takes over. */
  class OriclePdpFold extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.left = this.querySelector('[data-o-fold-left]');
      this.panel = this.querySelector('[data-o-fold-panel]');
      this.wide = window.matchMedia('(min-width: 768px)');
      this.check = () => {
        if (!this.wide.matches) { this.style.removeProperty('--o-left-top'); if (this.panel) this.panel.classList.remove('is-pinned'); return; }
        const top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-top')) || 104;
        const vh = window.innerHeight;
        if (this.left) this.style.setProperty('--o-left-top', Math.min(top, vh - this.left.offsetHeight - 24) + 'px');
        if (this.panel) this.panel.classList.toggle('is-pinned', this.panel.offsetHeight + top + 24 <= vh);
      };
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(this.check); if (this.left) this.ro.observe(this.left); if (this.panel) this.ro.observe(this.panel); }
      window.addEventListener('resize', this.check);
      this.check();
    }
    disconnectedCallback() { if (this.ro) this.ro.disconnect(); window.removeEventListener('resize', this.check); }
  }
  if (!customElements.get('oricle-pdp-fold')) customElements.define('oricle-pdp-fold', OriclePdpFold);

  /* ClampText: long text clamped to --lines (CSS) with a Read more / Show less disclosure that appears only when the text
     actually overflows. The source text is never cut. */
  class OricleClamp extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.text = this.querySelector('[data-o-clamp-text]');
      this.btn = this.querySelector('[data-o-clamp-btn]');
      if (!this.text || !this.btn) return;
      this.label = this.btn.querySelector('[data-o-clamp-label]') || this.btn.querySelector('span');
      this.btn.addEventListener('click', () => this.toggle());
      this.check = () => { if (!this.classList.contains('is-open')) this.btn.hidden = !(this.text.scrollHeight - this.text.clientHeight > 2); };
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(this.check); this.ro.observe(this.text); }
      this.check();
      // A clamped box keeps its height when the web font swaps in; re-measure then (same as oricle-components.js).
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (this.isConnected) this.check(); });
    }
    disconnectedCallback() { if (this.ro) this.ro.disconnect(); }
    toggle() {
      const open = !this.classList.contains('is-open');
      this.classList.toggle('is-open', open);
      this.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this.label) this.label.textContent = open ? (this.btn.dataset.less || this.btn.dataset.more) : this.btn.dataset.more;
      if (!open) this.check();
    }
  }
  if (!customElements.get('oricle-clamp')) customElements.define('oricle-clamp', OricleClamp);

  /* What's in the box: a row lights its numbered pin on hover or focus; a tap keeps it lit (PpKit). Rows are buttons. */
  class OricleKit extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.rows = Array.from(this.querySelectorAll('[data-o-kit-row]'));
      this.pins = Array.from(this.querySelectorAll('[data-o-kit-pin]'));
      this.held = -1;
      const at = (e) => { const r = e.target.closest('[data-o-kit-row]'); return r ? parseInt(r.dataset.oKitRow, 10) : -1; };
      this.addEventListener('pointerover', (e) => { const k = at(e); if (k > -1) this.light(k); });
      this.addEventListener('pointerout', (e) => { if (at(e) > -1) this.light(this.held); });
      this.addEventListener('focusin', (e) => { const k = at(e); if (k > -1) this.light(k); });
      this.addEventListener('focusout', () => this.light(this.held));
      this.addEventListener('click', (e) => { const k = at(e); if (k < 0) return; this.held = this.held === k ? -1 : k; this.light(this.held === -1 ? k : this.held); });
    }
    light(k) {
      this.rows.forEach((r) => { const on = parseInt(r.dataset.oKitRow, 10) === k; r.classList.toggle('is-on', on); r.setAttribute('aria-pressed', parseInt(r.dataset.oKitRow, 10) === this.held ? 'true' : 'false'); });
      this.pins.forEach((p) => p.classList.toggle('is-on', parseInt(p.dataset.oKitPin, 10) === k));
    }
  }
  if (!customElements.get('oricle-kit')) customElements.define('oricle-kit', OricleKit);

  /* Customer clips (ClipRail): nothing autoplays; a tap on a clip that has a video swaps its poster for the inline player
     with controls, and one plays at a time. A card with no video is a plain still, not a control. */
  class OriclePdpClips extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-o-clip-play]'); if (!b) return;
        const card = b.closest('[data-o-clip]'); const tpl = card && card.querySelector('template'); if (!tpl) return;
        this.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });
        const frag = tpl.content.cloneNode(true); const v = frag.querySelector('video');
        card.classList.add('is-playing'); b.replaceWith(frag);
        if (v) { v.focus(); const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      });
      this.addEventListener('play', (e) => { this.querySelectorAll('video').forEach((v) => { if (v !== e.target && !v.paused) v.pause(); }); }, true);
    }
  }
  if (!customElements.get('oricle-pdp-clips')) customElements.define('oricle-pdp-clips', OriclePdpClips);

  /* In-page asks (the chapter closes, data-o-page-add) press the one buy form's real submit button, like the dock. */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-o-page-add]'); if (!b) return;
    if (!(window.Oricle && window.Oricle.submitBuyForm && window.Oricle.submitBuyForm(b))) window.location.hash = '#buy';
  });

})();
